import { formatCurrency, getDeptColor, getDeptShortName, MONTH_TOTAL_ROW_ID, SHOW_TABLE_AMOUNT_COLORS } from './deptDisplay';

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : '100, 116, 139';
};

// Header/cell text is forced single-line with ellipsis by the shared table's CSS
// (GlobalTableNew.css - not something to change globally, it's used everywhere), so the only
// way to actually show full data with no truncation is to size each column to its own content
// - the longer of the header label or the widest value it'll hold - rather than one fixed
// width for every column regardless of what's in it.
// `padding` defaults to just the cell's own horizontal padding (see the matching `paddingLeft/
// Right: 8px` override in DepartmentIncomeTracker.jsx) - that's all a data cell needs. A header
// cell additionally carries the freeze-pin button and sort arrows (see `.th-left`/`.th-right`
// in GlobalTableNew.css), so every header-label estimate below passes HEADER_CHROME_PADDING
// explicitly. Getting this split right (rather than assuming every column needs chrome-sized
// padding) is most of the difference between the table fitting a normal laptop screen and not.
// Every column (Date, each department, Total) gets this same width, so the cells are evenly
// distributed - the table is `table-layout: fixed; width: 100%`, so equal px widths also
// stretch to equal shares of whatever width is available.
const COL_WIDTH = 110;

const clickableCellSx = {
  cursor: 'pointer',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textDecorationColor: 'transparent',
  '&:hover': { textDecorationColor: 'currentColor' },
};

// Same emerald "money" accent as the income-detail popup's Total Revenue, but restrained here -
// this column sits next to N department columns each with their own (stronger) heatmap tint, so
// it only needs to read as "the summary column, subtly colored by how big the day was," not
// compete with them. A thin left border does the "this one's different" work instead of a loud
// fill; the tint itself stays capped low even on the month's biggest day.
const TOTAL_RGB = '5, 150, 105';

// `departments` = meta.departments (already sorted into display order). `maxByDept` = the
// highest single-day amount seen for each dept this month, used both to scale the heatmap
// tint (so "today's ₹40K for Classroom" reads as strong when Classroom's days rarely clear
// ₹50K) and to size that column to its widest real value.
// `todayDate` highlights the current day's row so it's the first thing a user's eye lands on.
// `onCellClick(date, deptId, deptName)`: every department cell and the Total Revenue cell are
// clickable - opens a dialog listing the actual income rows behind that number. `deptId=null`
// for the Total column (every row for that day, matching the now-inclusive `total`).
// Column widths are plain px, sized to whichever is wider: the (short) header label or the
// widest value the column will hold. A previous version normalized these to percentages so
// the whole table always fit one viewport with no scrolling - but with a realistic number of
// department columns that meant every column got squeezed below its content's minimum, and
// long headers ended up hard-wrapped mid-word into a column of single letters. Natural pixel
// widths plus the shared table's own horizontal scroll (frozen Date column, see
// GlobalTableNew.css `.sticky-left`/`.frozen-col`) degrades far better: on a wide screen
// everything still fits with room to spare, and on a narrow one it scrolls instead of mangling.
const isTotalRow = (row) => row.date === MONTH_TOTAL_ROW_ID;

// `options` (optional): { format(value, column) - how an amount is written in a cell (`column` = the department id, or 'total') (the tooltip always keeps the full amount), headerName(dept, shortName) -
// what a department column is called, todaySuffix - false drops the "· Today" after today's date }. A small screen uses them to write 5,24,318 as 5.24L and Foundation as Found.; unset = as before.
export const departmentIncomeTrackerTableHeader = (departments = [], maxByDept = {}, todayDate = null, maxTotal = 0, onCellClick = () => {}, options = {}) => {
  const { format = formatCurrency, headerName, todaySuffix = true } = options;
  const todayStyle = (row) =>
    row.date === todayDate ? { boxShadow: 'inset 0 0 0 9999px rgba(99, 102, 241, 0.07)' } : undefined;

  // The month-total footer row isn't a real day - no click-through-to-detail, and a distinct
  // bold/shaded look so it reads as a summary, not one more row in the list.
  const totalRowStyle = (row) =>
    isTotalRow(row) ? { fontWeight: 700, background: '#F1F5F9', borderTop: '2px solid #CBD5E1' } : undefined;

  return [
    {
      field: 'dayLabel',
      headerName: 'Date',
      // Sized to the everyday "DD/MM" content, not the rare "DD/MM · Today" suffix (only ever
      // shown on one row this month) - reserving extra width in every one of 9 columns for a
      // decoration that appears on 1 row out of 31 isn't worth it; that one row's cell just
      // ellipsis-truncates the suffix instead, same as it would if the number itself were long.
      width: COL_WIDTH,
      sortable: true,
      renderCell: ({ row }) => (row.date === todayDate && todaySuffix ? `${row.dayLabel} · Today` : row.dayLabel),
      cellStyle: ({ row }) => ({
        fontWeight: row.date === todayDate ? 700 : 500,
        ...todayStyle(row),
        ...totalRowStyle(row),
      }),
    },
    ...departments.map((dept) => {
      const color = getDeptColor(dept.deptId);
      const rgb = hexToRgb(color);
      const max = maxByDept[dept.deptId] || 0;
      const shortName = getDeptShortName(dept.deptId, dept.deptName);
      return {
        field: String(dept.deptId),
        headerName: headerName ? headerName(dept, shortName) : shortName,
        // Full name is never lost - just a hover away - so the header itself can stay short
        // enough to render on one (or, for a two-word short name, two wrapped-at-a-space)
        // line instead of needing the column widened to fit "Operations Growth Department
        // (OGD)" in full.
        headerTooltip: dept.deptName !== shortName ? dept.deptName : undefined,
        width: COL_WIDTH,
        align: 'right',
        sortable: true,
        valueGetter: ({ value }) => format(value, String(dept.deptId)),
        toolTipGetter: ({ value }) => formatCurrency(value),
        renderCell: ({ row, value }) =>
          isTotalRow(row) ? (
            <span>{value}</span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              style={clickableCellSx}
              onClick={() => onCellClick(row.date, dept.deptId, dept.deptName)}
              onKeyDown={(e) => e.key === 'Enter' && onCellClick(row.date, dept.deptId, dept.deptName)}
            >
              {value}
            </span>
          ),
        cellStyle: ({ row }) => {
          // Raw number, not `value` - valueGetter already turned that into a formatted
          // currency string ("₹ 1,23,456"), which Number() can't parse.
          const raw = Number(row[String(dept.deptId)] || 0);
          const intensity = max > 0 ? Math.min(raw / max, 1) : 0;
          return {
            background: SHOW_TABLE_AMOUNT_COLORS && intensity > 0
              ? `rgba(${rgb}, ${(0.06 + intensity * 0.28).toFixed(2)})`
              : undefined,
            ...todayStyle(row),
            ...totalRowStyle(row),
          };
        },
      };
    }),
    {
      field: 'total',
      headerName: 'Total',
      headerTooltip: 'Total Revenue',
      width: COL_WIDTH,
      align: 'right',
      sortable: true,
      valueGetter: ({ value }) => format(value, 'total'),
      toolTipGetter: ({ value }) => formatCurrency(value),
      renderCell: ({ row, value }) =>
        isTotalRow(row) ? (
          <span>{value}</span>
        ) : (
          <span
            role="button"
            tabIndex={0}
            style={clickableCellSx}
            onClick={() => onCellClick(row.date, null, 'Total Revenue')}
            onKeyDown={(e) => e.key === 'Enter' && onCellClick(row.date, null, 'Total Revenue')}
          >
            {value}
          </span>
        ),
      cellStyle: ({ row }) => {
        const intensity = maxTotal > 0 ? Math.min(Number(row.total || 0) / maxTotal, 1) : 0;
        return {
          fontWeight: 700,
          borderLeft: `2px solid rgba(${TOTAL_RGB}, 0.35)`,
          background: SHOW_TABLE_AMOUNT_COLORS
            ? `rgba(${TOTAL_RGB}, ${(0.03 + intensity * 0.11).toFixed(2)})`
            : undefined,
          ...todayStyle(row),
          ...totalRowStyle(row),
        };
      },
    },
  ];
};
